import React, { useState } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';

interface SendAppNotificationProps {
  userId: Id<"users">;
  hasPushToken: boolean;
}

const TITLE_MAX = 100;
const BODY_MAX = 500;

// Sends a message to the customer's mobile app inbox and, if their phone is
// registered and their preferences allow it, as a push notification.
const SendAppNotification: React.FC<SendAppNotificationProps> = ({ userId, hasPushToken }) => {
  const sendToUser = useMutation(api.notifications.adminSendToUser);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setStatus(null);
    try {
      const result = await sendToUser({ userId, title, body, route: '/inbox' });
      if (result.success) {
        setTitle('');
        setBody('');
        setStatus({ ok: true, text: 'Sent to the customer’s app inbox.' });
      } else {
        setStatus({ ok: false, text: result.error || 'The notification was not sent.' });
      }
    } catch (error) {
      console.error('Send app notification failed:', error);
      setStatus({ ok: false, text: 'The notification was not sent. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
      <h2 className="text-xl font-medium font-sans mb-1">App Notification</h2>
      <p className="text-sm text-gray-600 mb-4">
        {hasPushToken
          ? 'This customer has the mobile app with notifications on. The message is pushed to their phone and kept in their app inbox.'
          : 'This customer has no phone registered for push. The message will wait in their app inbox until they next open the app.'}
      </p>
      <form onSubmit={handleSend} className="space-y-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX}
          placeholder="Title"
          className="w-full px-3 py-2 border border-gray-300 rounded"
          required
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={BODY_MAX}
          placeholder="Message"
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded"
          required
        />
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={sending || !title.trim() || !body.trim()}
            className="px-4 py-2 bg-[#16A34A] text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send notification'}
          </button>
          <span className="text-sm text-gray-500">{body.length}/{BODY_MAX}</span>
          {status && (
            <span className={`text-sm ${status.ok ? 'text-green-700' : 'text-red-600'}`}>{status.text}</span>
          )}
        </div>
      </form>
    </div>
  );
};

export default SendAppNotification;
